import { Injectable, inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { RoleService } from './role.service';

export interface DecodedToken {
  realm_access?: {
    roles: string[];
  };
  resource_access?: Record<
    string,
    {
      roles: string[];
    }
  >;
  [key: string]: unknown;
}

/**
 * Production role service using OAuthService.
 * Implements RoleService for production authentication flow.
 */
@Injectable()
export class ProdRoleService extends RoleService {
  private oauthService = inject(OAuthService);

  override readonly userRoles$ = new BehaviorSubject<string[]>([]);

  override updateRolesFromToken(): void {
    const roles = this.getUserRoles();
    this.userRoles$.next(roles);
  }

  override getUserRoles$(): BehaviorSubject<string[]> {
    return this.userRoles$;
  }

  override getCallerUserId(): string {
    const accessToken = this.oauthService.getAccessToken();
    if (!accessToken) return '';
    try {
      const decoded = jwtDecode<{ sub?: string }>(accessToken);
      return decoded.sub ?? '';
    } catch {
      return '';
    }
  }

  /**
   * Retrieves the user roles from the access token.
   * Supports both realm_access.roles and resource_access roles.
   * @returns An array of user role strings.
   */
  private getUserRoles(): string[] {
    const accessToken = this.oauthService.getAccessToken();
    if (!accessToken) {
      console.warn('No access token available for role extraction');
      return [];
    }
    try {
      const decoded: DecodedToken = jwtDecode(accessToken);
      console.log('Decoded token:', JSON.stringify(decoded));

      // Get realm roles (global roles)
      let roles = decoded?.realm_access?.roles || [];

      // Also add resource-specific roles if they exist
      if (decoded?.resource_access) {
        Object.values(decoded.resource_access).forEach((resource) => {
          if (resource?.roles && Array.isArray(resource.roles)) {
            roles = [...roles, ...resource.roles];
          }
        });
      }

      console.log('Extracted roles:', roles);
      return roles;
    } catch (e) {
      console.error('Failed to decode token:', e);
      return [];
    }
  }
}
